import { z } from 'zod';
import { MENU_ROLES } from './menu-config.zod';

export const ANNOUNCEMENT_LEVELS = ['info', 'warning', 'critical'] as const;
export type AnnouncementLevel = (typeof ANNOUNCEMENT_LEVELS)[number];
export const MAX_ANNOUNCEMENTS = 10;

const isoDate = z.string().datetime({ offset: true });

/** Annonce affichée en bandeau dans l'application (espace /superadmin). */
export const AnnouncementSchema = z
  .object({
    id: z.string().min(1).max(64),
    message: z.string().trim().min(3, 'Message trop court').max(500),
    level: z.enum(ANNOUNCEMENT_LEVELS),
    /** Rôles qui voient l'annonce ; vide = tout le monde. */
    roles: z.array(z.enum(MENU_ROLES)).max(MENU_ROLES.length),
    starts_at: isoDate.nullable(),
    ends_at: isoDate.nullable(),
    /** L'utilisateur peut la masquer (elle revient si elle est modifiée). */
    dismissible: z.boolean(),
    updated_at: isoDate,
  })
  .refine((a) => !a.starts_at || !a.ends_at || new Date(a.starts_at) < new Date(a.ends_at), {
    message: 'La fin doit être après le début',
    path: ['ends_at'],
  });
export type Announcement = z.infer<typeof AnnouncementSchema>;

export const AnnouncementsUpdateSchema = z.array(AnnouncementSchema).max(MAX_ANNOUNCEMENTS, `${MAX_ANNOUNCEMENTS} annonces maximum`);

/** Annonces à afficher maintenant pour un rôle (fenêtre de dates et rôles ciblés). */
export function activeAnnouncementsFor(announcements: Announcement[], role: string | null | undefined, now = new Date()): Announcement[] {
  return announcements.filter(
    (a) =>
      (!a.starts_at || new Date(a.starts_at) <= now) &&
      (!a.ends_at || new Date(a.ends_at) > now) &&
      (a.roles.length === 0 || (!!role && (a.roles as readonly string[]).includes(role))),
  );
}

export const MaintenanceSchema = z.object({
  enabled: z.boolean(),
  message: z.string().trim().max(500),
  /** Fin prévue, affichée aux utilisateurs (informatif : la maintenance se termine quand elle est désactivée). */
  ends_at: isoDate.nullable(),
});
export type Maintenance = z.infer<typeof MaintenanceSchema>;

export const DEFAULT_MAINTENANCE: Maintenance = { enabled: false, message: '', ends_at: null };

/** Code renvoyé par l'API (503) pendant une maintenance : le dashboard l'utilise pour afficher la page dédiée. */
export const MAINTENANCE_ERROR_CODE = 'MAINTENANCE';
